import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { ReportButton } from "@/components/ReportButton";
import { getFeed, type FeedItem } from "@/api/composer";

/**
 * The Community feed — membership before ranking.
 *
 * The scope toggle is the point of this page. `mine` is the set of posts whose
 * audience includes you; `everywhere` is the wider room. The server never
 * auto-widens: if `mine` is thin, it stays thin rather than quietly padding
 * itself with posts that aren't for you. Being able to flip between the two and
 * see the difference is how you tell the audience engine is doing anything at
 * all.
 *
 * Ranking happens *after* that membership cut, never instead of it — which is
 * the affinity-vs-barrier rule in its feed form: your identity moves things up
 * the list, it does not decide what exists.
 */
export default function ComposerFeedLab() {
  const [scope, setScope] = useState<"mine" | "everywhere">("mine");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["composer-feed", scope],
    queryFn: () => getFeed({ scope, limit: 20 }),
  });

  const feed = data?.data;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Community feed"
        subtitle="Everything whose audience includes you — ranked after that cut, never instead of it."
        breadcrumbs={[
          { label: "Lab", href: "/" },
          { label: "Composer", href: "/lab/composer" },
          { label: "Feed" },
        ]}
        actions={
          <Link to="/lab/composer">
            <Button variant="secondary" size="sm">
              Compose
            </Button>
          </Link>
        }
      />

      {/* ── scope ───────────────────────────────────────────────── */}
      <div className="mb-5 inline-flex rounded-full border border-gray-200 bg-white p-1">
        {(["mine", "everywhere"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              scope === s
                ? "bg-gray-900 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {s === "mine" ? "You'd know" : "Everywhere"}
          </button>
        ))}
      </div>
      <p className="mb-5 -mt-2 text-xs text-gray-500">
        {scope === "mine"
          ? "Posts whose audience includes you. If this is empty, it stays empty — the server will not widen it for you."
          : "The wider room, including posts not aimed at you."}
      </p>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : "Feed failed to load"}
        </div>
      )}

      {feed && feed.items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
          <p className="font-medium text-gray-900">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            {scope === "mine"
              ? "No posts are aimed at someone with your identity right now. That is a real answer, not a loading state — try Everywhere to see what else is around."
              : "No community posts exist yet. Compose one."}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {feed?.items.map((item) => (
          <FeedCard key={item.id} item={item} />
        ))}
      </div>

      {feed?.pagination.hasMore && (
        <p className="mt-5 text-xs text-gray-400">
          More available — the Lab shows the first {feed.pagination.limit}.
        </p>
      )}
    </div>
  );
}

function FeedCard({ item }: { item: FeedItem }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <Link
          to={`/lab/voices/${item.slug || item.id}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {item.title}
        </Link>
        <div className="flex shrink-0 gap-1.5">
          <Badge variant="gray">{item.category}</Badge>
          {item.intentType && item.intentType !== "discussion" && (
            <Badge variant="blue">{item.intentType}</Badge>
          )}
        </div>
      </div>

      {item.description && (
        <p className="mt-1.5 text-sm text-gray-600">{item.description}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {/*
         * Anonymous posts arrive with `user: null` AND `originState: null` from
         * the server. The Lab renders exactly that rather than reaching for a
         * fallback — if a name ever appears here, the anonymity contract broke
         * upstream and this is where it shows.
         */}
        <span>{item.isAnonymous ? "Anonymous" : (item.user?.name ?? "—")}</span>
        {item.originState && <span>· from {item.originState}</span>}
        {item.city && (
          <span>
            · {item.city}
            {item.state ? `, ${item.state}` : ""}
          </span>
        )}
        <span>· {item.standCount} stands</span>
        <span>· {item.commentCount} comments</span>
        <span className="text-gray-400">· {item.topicScope}</span>
        {/* Feed items are all voices today (the feed reads the voices table),
            so the report is typed as one. */}
        <span className="ml-auto">
          <ReportButton contentType="voice" contentId={item.id} />
        </span>
      </div>
    </article>
  );
}
